import React, { useState } from 'react';
import { getTranslation } from '../i18n';

// Tiny interpolation helper for i18n strings with {placeholder} tokens.
const fmt = (s, vars = {}) =>
  Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

const CreateBoardModal = ({ onClose, onCreate, lang = 'en' }) => {
  // Resolved translation bundle for the active workspace language.
  // Falls back to English automatically inside getTranslation if the
  // requested locale is missing.
  const t = getTranslation(lang);

  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [expirationDays, setExpirationDays] = useState(14);
  const [lockDaysBefore, setLockDaysBefore] = useState(5);
  // Board type — default "client" (the full collaborative workspace).
  // The user can switch to "inspiration" for a presentation-only board.
  const [boardType, setBoardType] = useState('client');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    await onCreate({
      name,
      client_name: clientName,
      session_date: sessionDate ? new Date(sessionDate).toISOString() : null,
      expiration_days: expirationDays,
      lock_days_before: lockDaysBefore,
      board_type: boardType,
    });

    setLoading(false);
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content fade-in">
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '24px',
          marginBottom: '24px',
          textAlign: 'center',
        }}>
          {t.cbm_title}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Board type selector — first decision so the rest of the
              form (and the eventual editor experience) reflects the
              chosen surface. Calm card-style radios, not native UI. */}
          <div className="form-group" data-testid="cbm-board-type-group">
            <label style={{ marginBottom: 10 }}>{t.cbm_board_type_label}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { value: 'client', label: t.cbm_board_type_client, desc: t.cbm_board_type_client_desc },
                { value: 'inspiration', label: t.cbm_board_type_inspiration, desc: t.cbm_board_type_inspiration_desc },
              ].map((opt) => {
                const checked = boardType === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setBoardType(opt.value)}
                    data-testid={`cbm-board-type-${opt.value}`}
                    aria-pressed={checked}
                    style={{
                      display: 'block',
                      textAlign: 'left',
                      padding: '14px 14px 12px',
                      background: checked ? 'var(--kahla-cream, #f6f1e7)' : 'transparent',
                      border: checked ? '1px solid #cf9a3d' : '1px solid var(--kahla-border)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'background 0.2s ease, border-color 0.2s ease',
                    }}
                  >
                    <div style={{
                      fontFamily: 'Playfair Display, serif',
                      fontSize: '14px',
                      color: 'var(--kahla-charcoal, #2a2620)',
                      letterSpacing: '-0.005em',
                      marginBottom: 4,
                    }}>{opt.label}</div>
                    <div style={{
                      fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                      fontStyle: 'italic',
                      fontSize: '12px',
                      color: 'var(--kahla-text-light)',
                      lineHeight: 1.45,
                    }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label>{t.cbm_board_name}</label>
            <input
              type="text"
              data-testid="board-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.cbm_board_name_placeholder}
              required
            />
          </div>

          <div className="form-group">
            <label>{t.cbm_client_name}</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder={t.cbm_client_name_placeholder}
              required
            />
          </div>

          <div className="form-group">
            <label>{t.cbm_project_date}</label>
            <input
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
            />
            <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '4px' }}>
              {t.cbm_autolock_hint}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>{t.cbm_link_expires}</label>
              <select
                value={expirationDays}
                onChange={(e) => setExpirationDays(parseInt(e.target.value))}
              >
                <option value={7}>{fmt(t.cbm_days, { n: 7 })}</option>
                <option value={14}>{fmt(t.cbm_days, { n: 14 })}</option>
                <option value={30}>{fmt(t.cbm_days, { n: 30 })}</option>
                <option value={60}>{fmt(t.cbm_days, { n: 60 })}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t.cbm_lock_before}</label>
              <select
                value={lockDaysBefore}
                onChange={(e) => setLockDaysBefore(parseInt(e.target.value))}
              >
                <option value={3}>{fmt(t.cbm_days, { n: 3 })}</option>
                <option value={5}>{fmt(t.cbm_days, { n: 5 })}</option>
                <option value={7}>{fmt(t.cbm_days, { n: 7 })}</option>
                <option value={30}>{fmt(t.cbm_days, { n: 30 })}</option>
                <option value={60}>{fmt(t.cbm_days, { n: 60 })}</option>
              </select>
            </div>
          </div>

          <p
            data-testid="create-board-editable-later-hint"
            style={{
              fontSize: '11px',
              color: 'var(--kahla-text-light)',
              fontStyle: 'italic',
              textAlign: 'center',
              marginTop: '14px',
              marginBottom: 0,
              lineHeight: 1.5,
            }}
          >
            {t.cbm_editable_later}
          </p>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              style={{ flex: 1 }}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              data-testid="create-board-submit"
              className="btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? t.cbm_creating : t.cbm_create_board}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateBoardModal;
